/* Knowledge tab — business knowledge scrape + KB editor (A7b-i) */
import React from 'react';
import { describeKbTruncation } from '../../kb-assistant/kbPromptBudget';
import KbAssistantPanel from './KbAssistantPanel';
import TestAIConversation from './TestAIConversation';

export default function KnowledgeTab({
  isMobile,
  formSettings,
  setFormSettings,
  aiWebsiteUrl,
  setAiWebsiteUrl,
  aiWebsiteUrls,
  setAiWebsiteUrls,
  setupingAi,
  kbContent,
  setKbContent,
  editingKb,
  setEditingKb,
  savingKb,
  newUrl,
  setNewUrl,
  addingUrl,
  aiReplyEnabled,
  setAiReplyEnabled,
  aiCalendarLink,
  setAiCalendarLink,
  setupKnowledgeFromUrls,
  saveKbEdits,
  applyKbDraft,
  selectedWorkspaceId,
  addUrlToKb,
  loadForm,
  workspaceKbs = [],
  loadingWorkspaceKbs = false,
  linkingKb = false,
  linkExistingKb = () => {},
  testLeadName,
  setTestLeadName,
  testLeadEmail,
  setTestLeadEmail,
  testLeadMessage,
  setTestLeadMessage,
  conversationHistory = [],
  setConversationHistory,
  setAiReplyPreview,
  testingAiReply,
  testAIReply,
  sendTestEmail,
}) {
  const scrapeDisabled = setupingAi || formSettings.knowledgeBaseId || (aiWebsiteUrls.length === 0 && !aiWebsiteUrl.trim());
  const truncationNotice = describeKbTruncation(kbContent);

  return (
    <div>
      <div className={`fb-card${isMobile ? ' is-mobile' : ''}`} style={{ marginBottom: '2rem' }}>
        <h2 className="fb-section-title">Knowledge base for this form</h2>
        <p className="fb-muted" style={{ marginBottom: '1rem' }}>
          Pick a knowledge base you already built (in the sidebar or another form), or scrape a new one below.
        </p>
        <label className="fb-label" htmlFor="kb-selector">
          Workspace knowledge base
        </label>
        <select
          id="kb-selector"
          className="fb-select"
          value={formSettings.knowledgeBaseId || ''}
          disabled={linkingKb || loadingWorkspaceKbs}
          onChange={(e) => {
            const nextId = e.target.value;
            if (nextId && nextId !== formSettings.knowledgeBaseId) {
              linkExistingKb(nextId);
            }
          }}
          style={{ marginTop: '0.5rem', opacity: (linkingKb || loadingWorkspaceKbs) ? 0.6 : 1 }}
        >
          <option value="">Select a knowledge base</option>
          {workspaceKbs.map((kb) => (
            <option key={kb.id} value={kb.id}>
              {kb.name}
            </option>
          ))}
        </select>
        {linkingKb && (
          <p className="fb-muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
            Linking...
          </p>
        )}
        {!loadingWorkspaceKbs && workspaceKbs.length === 0 && (
          <p className="fb-muted" style={{ fontSize: '0.85rem', marginTop: '0.75rem' }}>
            No knowledge bases yet. Scrape one below or create one in the sidebar.
          </p>
        )}
      </div>

      <div className={`fb-card${isMobile ? ' is-mobile' : ''}`} style={{ marginBottom: '2rem' }}>
        <div className="fb-header-row">
          <h2 className="fb-section-title" style={{ marginBottom: 0 }}>Or build a new one</h2>
          {formSettings.knowledgeBaseId && (
            <button
              type="button"
              className="fb-btn-ghost-sm"
              onClick={() => {
                setFormSettings({ ...formSettings, knowledgeBaseId: '' });
                setAiReplyEnabled(false);
                setAiWebsiteUrl('');
                setAiWebsiteUrls([]);
                setAiCalendarLink('');
                setKbContent('');
              }}
            >
              Edit Setup
            </button>
          )}
        </div>
        <p className="fb-muted" style={{ marginBottom: '1.5rem' }}>
          Scrape your website once to build a knowledge base for AI replies, chat, and spam detection. The same content is used everywhere.
        </p>

        <div style={{ marginBottom: '1.5rem' }}>
          <label className="fb-label">
            Your Website URLs
          </label>
          <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Add multiple pages for comprehensive knowledge. Recommended: homepage, services, pricing, about, FAQ
          </p>
          <div className="fb-url-row">
            <div className={`fb-url-input-group${isMobile ? ' is-mobile' : ''}`}>
              <input
                type="url"
                className="fb-input"
                placeholder="https://yourbusiness.com"
                value={aiWebsiteUrl}
                onChange={(e) => setAiWebsiteUrl(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && aiWebsiteUrl) {
                    setAiWebsiteUrls([...aiWebsiteUrls, aiWebsiteUrl]);
                    setAiWebsiteUrl('');
                  }
                }}
                style={{ flex: 1, minWidth: 0 }}
              />
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={() => {
                  if (aiWebsiteUrl) {
                    setAiWebsiteUrls([...aiWebsiteUrls, aiWebsiteUrl]);
                    setAiWebsiteUrl('');
                  }
                }}
                disabled={!aiWebsiteUrl}
                style={{ flexShrink: 0, opacity: aiWebsiteUrl ? 1 : 0.5, cursor: aiWebsiteUrl ? 'pointer' : 'not-allowed' }}
              >
                Add
              </button>
            </div>
            <button
              type="button"
              className="inbox-pill-btn-primary"
              onClick={setupKnowledgeFromUrls}
              disabled={scrapeDisabled}
              style={{
                minHeight: isMobile ? 44 : undefined,
                whiteSpace: 'nowrap',
                opacity: scrapeDisabled ? 0.55 : 1,
                cursor: scrapeDisabled ? 'not-allowed' : 'pointer',
                ...(isMobile ? { width: '100%', flexBasis: '100%' } : {}),
              }}
            >
              {formSettings.knowledgeBaseId
                ? 'Knowledge base linked'
                : setupingAi
                  ? `Scraping ${aiWebsiteUrls.length || 1}...`
                  : `Scrape and build knowledge base (${aiWebsiteUrls.length || 1} page${aiWebsiteUrls.length > 1 ? 's' : ''})`}
            </button>
          </div>

          {aiWebsiteUrls.length > 0 && (
            <div className="fb-callout info" style={{ marginTop: '0.75rem' }}>
              <div className="fb-label" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                Pages to scrape ({aiWebsiteUrls.length}):
              </div>
              {aiWebsiteUrls.map((url, i) => (
                <div key={i} className="fb-list-row">
                  <span style={{ fontSize: '0.85rem', color: 'var(--ink)' }}>{url}</span>
                  <button
                    type="button"
                    className="fb-icon-remove"
                    onClick={() => setAiWebsiteUrls(aiWebsiteUrls.filter((_, index) => index !== i))}
                    aria-label="Remove URL"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          <p className="fb-muted" style={{ fontSize: '0.8rem', marginTop: '0.75rem' }}>
            Add multiple pages (homepage, /services, /about, /pricing) for better AI answers and spam detection
          </p>
        </div>
      </div>

      {/* KNOWLEDGE BASE EDITOR */}
      {formSettings.knowledgeBaseId && kbContent && (
        <div className="fb-card" style={{ marginBottom: '2rem' }}>
          <div className="fb-header-row">
            <h3 className="fb-subsection-title">Knowledge Base Content</h3>
            <button
              type="button"
              className={editingKb ? 'inbox-pill-btn' : 'inbox-pill-btn-primary'}
              onClick={() => setEditingKb(!editingKb)}
            >
              {editingKb ? 'View Mode' : 'Edit KB'}
            </button>
          </div>

          <p className="fb-muted" style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>
            {editingKb ? 'Edit the knowledge base used for AI replies, chat, and spam detection:' : 'This is what the system knows about your business:'}
          </p>
          {truncationNotice && (
            <div className="fb-callout warn" data-testid="kb-truncation-notice" style={{ marginBottom: '1rem' }}>
              {truncationNotice}
            </div>
          )}

          {editingKb ? (
            <div>
              <textarea
                className="fb-textarea"
                value={kbContent}
                onChange={(e) => setKbContent(e.target.value)}
                rows={15}
                style={{ marginBottom: '1rem' }}
              />
              <div className="fb-actions-row">
                <button
                  type="button"
                  className="inbox-pill-btn-primary"
                  onClick={saveKbEdits}
                  disabled={savingKb}
                  style={{ opacity: savingKb ? 0.6 : 1, cursor: savingKb ? 'not-allowed' : 'pointer' }}
                >
                  {savingKb ? 'Saving...' : 'Save Changes'}
                </button>
                <button
                  type="button"
                  className="inbox-pill-btn"
                  onClick={() => {
                    setEditingKb(false);
                    loadForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="fb-kb-preview">
              {kbContent}
            </div>
          )}

          {/* Add More URLs */}
          <div className="fb-callout info" style={{ marginTop: '1.5rem' }}>
            <label className="fb-label">
              Add More Pages to Knowledge Base
            </label>
            <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
              Add URLs to other pages (services, pricing, about, FAQ, etc.) to give AI more knowledge.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                type="url"
                className="fb-input"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://yourdomain.com/services"
                disabled={addingUrl}
                style={{ flex: 1, minWidth: 200 }}
              />
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={addUrlToKb}
                disabled={!newUrl || addingUrl}
                style={{ opacity: (!newUrl || addingUrl) ? 0.55 : 1, cursor: (!newUrl || addingUrl) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {addingUrl ? 'Adding...' : 'Add URL'}
              </button>
            </div>
          </div>
        </div>
      )}

      {formSettings.knowledgeBaseId && kbContent && (
        <KbAssistantPanel
          kbContent={kbContent}
          scrapedPageUrls={
            aiWebsiteUrls.length
              ? aiWebsiteUrls
              : (formSettings.websiteUrls || [])
          }
          workspaceId={selectedWorkspaceId || formSettings.workspaceId}
          onApplyDraft={typeof applyKbDraft === 'function' ? applyKbDraft : undefined}
          onHandoffTestQuestion={
            typeof setTestLeadMessage === 'function'
              ? (question) => {
                  setTestLeadMessage(question);
                  if (typeof document !== 'undefined') {
                    document.getElementById('test-ai-conversation')?.scrollIntoView({
                      behavior: 'smooth',
                      block: 'start',
                    });
                  }
                }
              : undefined
          }
        />
      )}

      {formSettings.knowledgeBaseId && kbContent && typeof testAIReply === 'function' && (
        <TestAIConversation
          testLeadName={testLeadName}
          setTestLeadName={setTestLeadName}
          testLeadEmail={testLeadEmail}
          setTestLeadEmail={setTestLeadEmail}
          testLeadMessage={testLeadMessage}
          setTestLeadMessage={setTestLeadMessage}
          conversationHistory={conversationHistory}
          setConversationHistory={setConversationHistory}
          setAiReplyPreview={setAiReplyPreview}
          testingAiReply={testingAiReply}
          onTest={testAIReply}
          onDownload={sendTestEmail}
          showDownload
        />
      )}
    </div>
  );
}
